/**
 * Pure space/area UOM name resolution + conversion for the property totals rules.
 *
 * Ported from the FE `@miq/utils` uom-calculator stack
 * (`projects/miq/utils/src/lib/uom-calculator/{units-definition,amount.helpers}.ts`):
 *   - the alias lists below are the FE `UOMS` area aliases verbatim (lower-cased match,
 *     as in `findUnit`),
 *   - the factors reproduce the FE unit definitions: SquareFeet/SquareMeter from `uom-units`
 *     (1 ft = 0.3048 m exactly), plus the FE's custom `Squared`/`timesNumber` units —
 *     note Acre is the FE's rounded 4046.86 m², NOT the survey acre 4046.8564224 m².
 *
 * `shared/area.ts` has no UOM-name-keyed conversion, so this lives in the property domain
 * (plan T2.6); promote to shared/ if another domain needs it.
 */
/** m² per square foot: 0.3048² exactly (uom-units SquareFeet). */
const SQM_PER_SQFT = 0.09290304;
/** m² per unit, keyed by canonical area-unit name. */
export const AREA_UNIT_FACTORS = {
  SquareFoot: SQM_PER_SQFT,
  SquareMeter: 1,
  SquareKilometer: 1000000,
  SquareMile: 1609.344 * 1609.344,
  Acre: 4046.86,
  Hectare: 10000 // Unit.timesNumber('Hectare', 10000, SquareMeter)
};
/**
 * FE `UOMS` area aliases (lower-cased, deduped). `findUnit` matches case-insensitively
 * against these — e.g. 'Square Feet', 'SF', 'sqm', 'SM' all resolve.
 */
export const SPACE_UOM_ALIASES = {
  SquareFoot: ['sf', 'square foot', 'sqf', 'square feet'],
  SquareMeter: ['sm', 'square meter', 'square meters', 'sqm', 'sqmt', 'sqmts', 'square mt', 'sq m'],
  SquareKilometer: ['skm', 'square kilometers', 'sqkm', 'sqkms', 'square km', 'sq km'],
  SquareMile: ['sml', 'square mile', 'square miles', 'sqml', 'sqmile', 'sq mi'],
  Acre: ['acre', 'acres', 'acr', 'ac'],
  Hectare: ['hectarea', 'hec', 'hectare', 'ha']
};
/**
 * Space UOM used when a mixed-UOM sum needs a target but the host supplied no
 * `deps.defaultSpaceUom`. Mirrors the FE `makeAmountWith` default unit (Units.SquareFeet),
 * spelled with the `Countries_Default_Space_UOM` vocabulary.
 */
export const FALLBACK_SPACE_UOM = 'Square Foot';
const aliasToFactor = new Map(Object.entries(SPACE_UOM_ALIASES).flatMap(([canonical, aliases]) => aliases.map(alias => [alias, AREA_UNIT_FACTORS[canonical]])));
/**
 * m² per unit for a UOM display name (any FE alias, case-insensitive), or undefined when the
 * name is not a known area unit (FE `findUnit` throws there; callers choose the fallback).
 */
export function resolveAreaFactor(uom) {
  return uom == null ? undefined : aliasToFactor.get(String(uom).toLowerCase());
}
/**
 * Convert an area `value` from `fromUom` to `toUom` (names resolved via {@link resolveAreaFactor}).
 *
 * Fallbacks mirror the FE mixed-UOM path (`makeAmountWith` → `getSafeUnit(name, defaultUnit)`):
 * an unrecognised `fromUom` is treated as already being in `toUom` (value passes through);
 * an unrecognised `toUom` also passes the value through (nothing sane to convert into).
 * Pure, no rounding — the totals are written unrounded, as in the source listeners.
 */
export function areaConvert(value, fromUom, toUom) {
  const toFactor = resolveAreaFactor(toUom);
  if (toFactor == null) {
    return value;
  }
  const fromFactor = resolveAreaFactor(fromUom) ?? toFactor;
  return value * (fromFactor / toFactor);
}
